import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@nyce/admin-kit/ui';
import { Page, TagsField, useAsync, useInterval } from '../../components/common';
import { TargetShell } from '../../identity/TargetShell';
import { TenantPicker } from '../../identity/TenantPicker';
import { activeTarget, activeTargetInfo, authClientFor, scim, tenantPath } from '../../identity/session';
import type { TenantRole } from '../../identity/api';
import { formatRelative } from '../../lib/format';
import { authProvisioningEndpoint, scimTenantUrl } from '../../pages/identity/ScimEndpoints';
import { scopesOf } from '../api/auth';
import { entraFiles } from '../generate/entra';
import { guidePath } from '../guides';
import { Wizard, type WizardStep } from '../wizard/Wizard';
import { FileOutput } from '../wizard/FileOutput';
import { useWizardParam } from '../wizard/params';
import { PLACEHOLDER, rememberSecret, useSecrets } from '../wizard/secrets';
import { Facts, Note, SecretValue, StepActions, useAction } from '../wizard/parts';

/** AUTH's `GET tenants/{id}/provisioning/status` (S4); null on an AUTH without it. */
interface ProvisioningStatus {
  lastActivityAt: string | null;
  users: { total: number; active: number; provisioned?: number };
  groups: number;
  hasProvisioningKey: boolean;
}

/** W3: SCIM from the customer's Entra ID into a Database tenant, through the environment's SCIM gateway. */
export function ScimWizardPage() {
  return (
    <Page
      title="SCIM (Entra ID)"
      description="Provision a customer's users and groups from Microsoft Entra ID: connect the tenant to the SCIM gateway, give the customer's admin the values for the enterprise app, wait for the first sync and map the provisioned groups to scopes."
    >
      <TargetShell service="scim">
        <ScimWizard />
      </TargetShell>
    </Page>
  );
}

function ScimWizard() {
  const target = activeTarget();
  const info = activeTargetInfo();
  const client = authClientFor(target);
  const secrets = useSecrets();
  const [tenantId, setTenantId] = useWizardParam('tenant');
  const [step] = useWizardParam('step');
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});

  const state = useAsync(async () => {
    if (!tenantId) return null;
    const [tenant, row, status, roles] = await Promise.all([
      client.tenants.get(tenantId),
      scim.get(tenantId).catch((e) => {
        if ((e as { status?: number }).status === 404) return null;
        throw e;
      }),
      client
        .request<ProvisioningStatus>('GET', `/tenants/${encodeURIComponent(tenantId)}/provisioning/status`)
        .catch(() => null),
      client.tenants.roles.list(tenantId),
    ]);
    return { tenant, row, status, roles };
  }, [target, tenantId]);
  const action = useAction(state.reload);
  const s = state.data;
  const tenant = s?.tenant;
  const expected = info && tenantId ? authProvisioningEndpoint(info.authUrl, tenantId) : '';
  const connected = s?.row?.target === 'auth' && s.row.endpoint === expected;
  const tenantUrl = info?.scimUrl && tenantId ? scimTenantUrl(info.scimUrl, tenantId) : '';
  const key = secrets(`scim:${target}:${tenantId}:key`);
  const synced = !!s?.status?.lastActivityAt || (s?.roles ?? []).some((r) => r.externalId);
  const provisioned = (s?.roles ?? []).filter((r) => r.externalId);
  const unmapped = provisioned.filter((r) => !r.scopes.length);
  const tenantScopes = scopesOf(tenant?.claims);
  useInterval(() => void state.reload(), 30_000, step === 'sync' && connected && !synced);

  const mapRole = (r: TenantRole) =>
    action.run(`role-${r.id}`, () =>
      client.tenants.roles.update(tenantId, r.id, { name: r.name, scopes: drafts[r.id] ?? [] })
    );

  const steps: WizardStep[] = [
    {
      id: 'tenant',
      title: 'Tenant',
      state: !tenant ? 'todo' : tenant.identityProvider === 'Database' ? 'done' : 'blocked',
      blocked: 'SCIM provisions into a Database tenant; this one signs users in at the WorkServer.',
      summary: tenant ? `${tenant.name} · ${tenant.identityProvider}` : undefined,
      body: (
        <>
          <p>
            SCIM users land in a <b>Database</b> tenant (AUTH keeps the users). No tenant yet? Create one with
            the{' '}
            <Link className="nyk-link" to="/onboarding/tenant">
              Customer & tenant wizard
            </Link>{' '}
            (users sign in at AUTH itself).
          </p>
          <TenantPicker target={target} value={tenantId} onChange={setTenantId} allowNone={false} />
        </>
      ),
    },
    {
      id: 'connect',
      title: 'Connect the gateway',
      state: tenant?.identityProvider !== 'Database' ? 'blocked' : connected ? 'done' : 'todo',
      blocked: 'Pick a Database tenant first.',
      summary: connected ? 'connected' : undefined,
      body: (
        <>
          <p>
            Issues the tenant's provisioning key (revoking the current one at once) and points the SCIM
            gateway at the tenant's AUTH provisioning API. Gateway replicas may keep the old route for up to
            10 minutes.
          </p>
          <Facts
            rows={[
              ['Tenant URL (for Entra)', <code key="u">{tenantUrl}</code>],
              [
                'Gateway route (internal, not for Entra)',
                s?.row ? `${s.row.target} → ${s.row.endpoint}` : 'none',
              ],
            ]}
          />
          {key && <SecretValue label="Secret Token (for Entra)" value={key} />}
          <StepActions error={action.error}>
            <Button
              variant={connected ? 'ghost' : 'primary'}
              loading={action.busy === 'connect'}
              onClick={() =>
                void action.run('connect', async () => {
                  const k = (await client.tenants.provisioningKeys.issue(tenantId)).provisioning_key;
                  rememberSecret(`scim:${target}:${tenantId}:key`, k);
                  await scim.put(tenantId, {
                    endpoint: expected,
                    target: 'auth',
                    trace: s?.row?.trace ?? false,
                  });
                })
              }
            >
              {connected ? 'Reconnect (new key)' : 'Connect SCIM'}
            </Button>
          </StepActions>
        </>
      ),
    },
    {
      id: 'entra',
      title: 'Entra ID enterprise app',
      state: !connected ? 'blocked' : synced ? 'done' : 'todo',
      blocked: 'Connect the gateway first.',
      body: (
        <>
          <p>
            The customer's Entra admin creates a non-gallery enterprise app with automatic provisioning and
            enters the Tenant URL and Secret Token.{' '}
            <Link
              className="nyk-link"
              to={guidePath('entra-scim', {
                customer: tenant?.customerName ?? undefined,
                tenantName: tenant?.name,
                tenantUrl,
              })}
            >
              Guide for the customer's admin
            </Link>
          </p>
          {!key && (
            <Note tone="warn">
              The Secret Token is only shown when issued: reconnect in the previous step to put a new one in
              the file.
            </Note>
          )}
          <FileOutput files={entraFiles(tenantUrl, key ?? PLACEHOLDER)} />
        </>
      ),
    },
    {
      id: 'sync',
      title: 'First sync',
      state: !connected ? 'blocked' : synced ? 'done' : 'waiting',
      blocked: 'Connect the gateway first.',
      summary: s?.status ? `${s.status.users.total} user(s), ${s.status.groups} group(s)` : undefined,
      body: (
        <>
          {s?.status ? (
            <Facts
              rows={[
                [
                  'Last provisioning call',
                  s.status.lastActivityAt ? formatRelative(s.status.lastActivityAt) : 'none yet',
                ],
                ['Users', `${s.status.users.total} (${s.status.users.active} active)`],
                ['Groups (roles)', String(s.status.groups)],
              ]}
            />
          ) : (
            <Facts rows={[['Provisioned groups', String(provisioned.length)]]} />
          )}
          {synced ? (
            <Note tone="good">Entra has provisioned into the tenant.</Note>
          ) : (
            <Note tone="warn">
              Waiting for Entra's first provisioning cycle (it starts within 40 minutes of "Start
              provisioning"; checked every 30 s).
            </Note>
          )}
        </>
      ),
    },
    {
      id: 'roles',
      title: 'Map groups to scopes',
      state: !synced ? 'blocked' : unmapped.length ? 'todo' : 'done',
      blocked: 'Wait for the first sync.',
      summary: provisioned.length
        ? `${provisioned.length - unmapped.length} of ${provisioned.length} mapped`
        : undefined,
      body: (
        <>
          <p>
            Each Entra group became a role without scopes: its members get nothing until it is mapped. Pick
            from the tenant's scopes ({tenantScopes.join(', ') || 'none — add claims first'}).
          </p>
          {provisioned.map((r) => (
            <div key={r.id} className="nyk-form" data-role={r.name}>
              <TagsField
                label={r.name}
                value={drafts[r.id] ?? r.scopes}
                onChange={(v) => setDrafts({ ...drafts, [r.id]: v })}
                placeholder={tenantScopes[0] ?? 'scope'}
                hint={r.scopes.length ? `now: ${r.scopes.join(', ')}` : 'no scopes yet'}
              />
              <StepActions>
                <Button
                  size="sm"
                  disabled={!drafts[r.id]}
                  loading={action.busy === `role-${r.id}`}
                  onClick={() => void mapRole(r)}
                >
                  Save
                </Button>
              </StepActions>
            </div>
          ))}
          <StepActions error={action.error}>
            {tenant && (
              <Link className="nyk-link" to={`${tenantPath(tenant.id)}&tab=roles`}>
                All roles of the tenant
              </Link>
            )}
          </StepActions>
        </>
      ),
    },
  ];

  return (
    <Wizard
      steps={steps}
      loading={state.loading && !!tenantId && !s}
      error={state.error}
      onRecheck={() => void state.reload()}
    />
  );
}
